/**
 * Invoice Service - Frontend service for invoice operations
 */

import api from "../../../shared/api.jsx";
import { getQuoteById } from "../../quotes/services/quoteService.js";
import { resolveQuoteItemPricing } from "../utils/resolveQuoteItemPricing.js";

/**
 * Get all invoices with optional filters
 * @param {Object} filters - Filter criteria
 * @returns {Promise<Object>} Invoices data
 */
export const getInvoices = async (filters = {}) => {
  try {
    const response = await api.get("/invoices", { params: filters });
    return {
      success: true,
      data: response.data || response,
      count: response.count || 0,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0,
    };
  }
};

/**
 * Get invoice by ID
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<Object>} Invoice data
 */
export const getInvoice = async (invoiceId) => {
  try {
    const response = await api.get(`/invoices/${invoiceId}`);
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Create invoice
 * @param {Object} invoiceData - Invoice data
 * @returns {Promise<Object>} Creation result
 */
export const createInvoice = async (invoiceData) => {
  try {
    const response = await api.post("/invoices", invoiceData);
    return {
      success: true,
      data: response.data || response,
      invoiceId: response.invoiceId || response.data?.id,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

export const appendInvoiceItems = async (invoiceId, items, appendRequestId) => {
  try {
    const response = await api.post(`/invoices/${invoiceId}/append-items`, {
      items,
      appendRequestId,
    });
    return { success: true, data: response.data || response };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Convert quote to invoice
 * @param {string} quoteId - Quote ID
 * @returns {Promise<Object>} Creation result
 */
export const convertQuoteToInvoice = async (quoteId) => {
  try {
    const response = await api.post(`/invoices/convert-from-quote/${quoteId}`);
    return {
      success: true,
      data: response.data || response,
      invoiceId:
        response.data?.invoiceId ||
        response.data?.data?.id ||
        response.invoiceId,
    };
  } catch (error) {
    // Handle 409 Conflict (invoice already exists)
    if (error.response?.status === 409) {
      return {
        success: false,
        error:
          error.response.data?.error || "Invoice already exists for this quote",
        invoiceId: error.response.data?.invoiceId,
        data: error.response.data?.data,
      };
    }
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Add payment to invoice
 * @param {string} invoiceId - Invoice ID
 * @param {Object} paymentData - Payment data
 * @returns {Promise<Object>} Update result
 */
export const addPayment = async (invoiceId, paymentData) => {
  try {
    const response = await api.post(
      `/invoices/${invoiceId}/payment`,
      paymentData
    );
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * List job-linked payments available to allocate onto this invoice
 */
export const getUnallocatedPayments = async (invoiceId) => {
  try {
    const response = await api.get(`/invoices/${invoiceId}/payments/unallocated`);
    return {
      success: true,
      data: response.data || response || [],
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      data: [],
    };
  }
};

/**
 * Upload POP for invoice payment and receive suggested fields
 */
export const uploadInvoicePaymentPop = async (invoiceId, files) => {
  try {
    const formData = new FormData();
    (files || []).forEach((file) => formData.append("popFiles", file));
    const response = await api.post(
      `/invoices/${invoiceId}/payment/pop`,
      formData
    );
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Mark invoice as sent
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<Object>} Update result
 */
export const markInvoiceAsSent = async (invoiceId) => {
  try {
    const response = await api.put(`/invoices/${invoiceId}/mark-sent`);
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Send invoice via email
 * @param {string} invoiceId - Invoice ID
 * @param {Object} options - Send options
 * @param {string} [options.customMessage] - Custom message to include
 * @param {string[]} [options.cc] - CC recipients
 * @param {string[]} [options.bcc] - BCC recipients
 * @returns {Promise<Object>} Send result
 */
export const sendInvoice = async (invoiceId, options = {}) => {
  try {
    const response = await api.post(`/invoices/${invoiceId}/send`, options);
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.message ||
        "Failed to send invoice",
    };
  }
};

/**
 * Update invoice
 * @param {string} invoiceId - Invoice ID
 * @param {Object} invoiceData - Invoice update data
 * @returns {Promise<Object>} Update result
 */
export const updateInvoice = async (invoiceId, invoiceData) => {
  try {
    const response = await api.put(`/invoices/${invoiceId}`, invoiceData);
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Cancel invoice
 * @param {string} invoiceId - Invoice ID
 * @param {Object} cancellationData - Cancellation data
 * @param {string} cancellationData.reason - Reason for cancellation
 * @param {string} cancellationData.cancelledBy - User/admin who cancelled
 * @returns {Promise<Object>} Cancellation result
 */
export const cancelInvoice = async (invoiceId, cancellationData = {}) => {
  try {
    const response = await api.put(
      `/invoices/${invoiceId}/cancel`,
      cancellationData
    );
    return {
      success: true,
      data: response.data || response,
      message: response.message || "Invoice cancelled successfully",
    };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.message ||
        "Failed to cancel invoice",
    };
  }
};

/**
 * Revise invoice - Create a new invoice based on an existing one
 * @param {string} invoiceId - Original invoice ID
 * @param {Object} revisionData - Revision data
 * @param {Object} revisionData.updates - Updates to apply to the new invoice
 * @param {boolean} revisionData.cancelOriginal - Whether to cancel the original invoice
 * @param {string} revisionData.reason - Reason for revision
 * @param {string} revisionData.revisedBy - User/admin who created the revision
 * @returns {Promise<Object>} Revision result
 */
export const reviseInvoice = async (invoiceId, revisionData = {}) => {
  try {
    const response = await api.post(
      `/invoices/${invoiceId}/revise`,
      revisionData
    );
    return {
      success: true,
      data: response.data || response,
      message: response.message || "Invoice revised successfully",
      newInvoiceId: response.data?.newInvoiceId,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.message ||
        "Failed to revise invoice",
    };
  }
};

/**
 * Load quote data for invoice form
 * @param {string} quoteId - Quote ID
 * @returns {Promise<Object>} Quote data formatted for invoice form
 */
export const loadQuoteForInvoice = async (quoteId) => {
  try {
    const response = await getQuoteById(quoteId);
    if (response.success && response.data) {
      const quote = response.data;
      
      // Check if quote is verified
      const isVerified =
        quote.status === "deposit_verified" ||
        quote.status === "deposit_paid" ||
        quote.status === "ordered" ||
        quote.payment?.depositVerified === true ||
        quote.payment?.depositPaid === true ||
        quote.status === "accepted";

      // ✅ CRITICAL: Preserve item pricing 1:1 from quote (don't redistribute)
      const items = (quote.items || []).map((item) => {
        const priced = resolveQuoteItemPricing(item, {
          quoteItemCount: quote.items?.length || 1,
          quoteTotal: quote.estimatedCost || quote.totalCost || 0,
        });

        return {
          description: item.description || "Item",
          quantity: priced.quantity,
          unitPrice: priced.unitPrice,
          total: priced.total,
          productId: item.productId || null,
          supplier: item.supplier || "bubakii",
          isCustom: !item.productId,
        };
      });

      // Calculate paid amount from quote deposit
      const depositAmount = quote.payment?.depositAmount || 
                           quote.payment?.proofData?.amount || 
                           quote.payment?.requiredAmount || 
                           0;
      const hasPaidDeposit = isVerified && depositAmount > 0;

      return {
        success: true,
        data: {
          customer: {
            customerId: quote.customerId,
            name: quote.customerName || "",
            phone: quote.customerPhone || "",
            email: "",
          },
          items: items.length > 0 ? items : [
            // Fallback: single item if no items exist
            {
              description: "Moving service",
              quantity: 1,
              unitPrice: quote.estimatedCost || quote.totalCost || 0,
              total: quote.estimatedCost || quote.totalCost || 0,
              productId: null,
              supplier: "bubakii",
              isCustom: true,
            },
          ],
          taxRate: 0, // No VAT by default - admin opts in per invoice
          dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0],
          notes: `Converted from quote ${quote.id || quote._id}`,
          quoteId: quote.id || quote._id,
          // Include quote status and verification info for validation
          quoteStatus: quote.status,
          isQuoteVerified: isVerified,
          // Include paid amount info
          paidAmount: hasPaidDeposit ? depositAmount : 0,
          depositAmount: depositAmount,
          leadData:
            quote.from || quote.to || quote.date
              ? {
                  from: quote.from,
                  to: quote.to,
                  date: quote.date,
                  items: quote.items,
                }
              : null,
        },
      };
    }
    return {
      success: false,
      error: "Quote not found",
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};
